import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, frames, glyphs } from "../../../Styles";
import { CheckBox, CheckStyles, TooltipString } from "../..";
import { TerminalDataProps, terminalsData } from "../../GlobalComponents/_GlobalComponents/editTerminalsData";
import { useEsfContext } from "../_EsfWalkThrough/EsfContext";
import { useDeviceInfo, useFeedComponents, useTerminals } from "../../../Services";

interface ActiveStationsCardsProps {
  mobile?: boolean;
  addCalVal?: boolean
}

export default function ActiveStationsCards({ mobile, addCalVal }: ActiveStationsCardsProps) {
  const { t } = useTranslation();
  const { state: { selectedStations, connection, domain, selectedMotors, locationResults }, dispatch } = useEsfContext();

  const { call: getDevices, data: deviceInfoState } = useDeviceInfo(connection);
  const { call: getTerminals, data: terminalsState, } = useTerminals(connection);
  const { call: getFeedComponentsInfo, data: feedComponentsState } = useFeedComponents(connection);
  
  useEffect(() => {
    getDevices({ fetchAmount: 500 }, connection);
    getTerminals({});
    getFeedComponentsInfo({fetchAmount: 50}, connection);
  }, [connection])

  const terminals = useMemo(() => terminalsState[domain] && terminalsState[domain].data, [domain, terminalsState])
  const devices = useMemo(() => deviceInfoState[domain] && deviceInfoState[domain].data, [deviceInfoState, domain])
  const feedComponents = useMemo(() => feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data: undefined,[domain, feedComponentsState])

  const stationData: TerminalDataProps[] = useMemo(() => {
    if (!terminals) return [];

    return Array.from(selectedStations ?? []).map(station => {
      const stationTerminals = station.terminals ?? [];
      const currentStationTerminals = terminals?.filter(terminal => stationTerminals.includes(terminal.terminal_id)).filter(t => t.fk_terminal_types === 83) ?? [];
      const terminalres = terminalsData({ terminals: currentStationTerminals, devices, t });

      return {
        locationName: { text: "station", value: station.name ?? "" },
        terminals: terminalres,
        visual: station.visual,
        location_id: station.station_id,
      };
    });
  }, [selectedStations, terminals, devices]);

  useEffect(() => {
    if (!terminals || !selectedStations) return;
    let updated = new Set<number>();
  
    selectedStations.forEach((station) => {
      const stationTerminals = station.terminals ?? [];
      const validTerminals = terminals.filter((t) => stationTerminals.includes(t.terminal_id) && t.fk_terminal_types === 83);
      const firstTerminal = validTerminals[0];
      
      if (firstTerminal) {
        updated.add(firstTerminal.terminal_id);
      }
  
      validTerminals.forEach((t) => {
        if (selectedMotors?.includes(t.terminal_id)) {
          updated.add(t.terminal_id);
        }
      });
    });
  
    const updatedArray = Array.from(updated);
  
    const isDifferent = (selectedMotors?.length ?? 0) !== updatedArray.length || selectedMotors?.some((id) => !updated.has(id));
  
    if (isDifferent) {
      dispatch({ selectedMotors: updatedArray });
    }
  }, [selectedStations, terminals]);

  if (!selectedStations || selectedStations?.length === 0) return;

  const _componentWidth = "113px"
  const _gridTemplate = addCalVal ? `60px ${_componentWidth} 60px 62px 15px` : `60px ${_componentWidth} 62px 15px`

  return (
    selectedStations.map((station) => {
      const data = stationData.find(s => s.location_id === station.station_id)?.terminals.filter(t => t.boardDisabled === false);
      if (!data) return; 

      return (
        <div key={`row_pen_${station.station_id}`} style={{
          backgroundColor: colors.greyLightSecondary,
          padding: mobile ? "0px 10px" : "10px 20px",
          display: "flex",
          alignItems: "start",
          justifyContent: "center",
          flexDirection: "column",
          width: "fit-content",
          boxSizing: "border-box",
          borderRadius: "10px"
        }}>
          <div style={{ whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: "5px", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary }}>
            {t("esfWalkThroughPages.esfWalkThrough.station", {defaultValue: "Station"})} {station.name}
          </div>

          <div style={{ display: "flex", flexDirection: "column", borderTop: "2px solid " + colors.greyTertiary, padding: "10px 0px 0px 0px", width: "100%" }}>
          
            <div style={{ display: "grid", gridTemplateColumns: _gridTemplate, gap: "10px", marginBottom: "2px"}}>
            <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary }}>{t("esfWalkThroughPages.esfWalkThrough.motor", {defaultValue: "motor"})}</p>
              <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary, padding: "0px 5px" }}>{t("esfWalkThroughPages.esfWalkThrough.component", {defaultValue: "component"})}</p>
              {addCalVal && <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary }}>{t("units.g", {defaultValue: "g"})}/{t("units.cup", {defaultValue: "cup"})}</p>}
              <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyPrimary, padding: "0px 5px" }}>{t("esfWalkThroughPages.esfWalkThrough.include", {defaultValue: "include"})}</p>
              <p style={{ }}></p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              {data.map((item, index) => {
                const component = feedComponents?.find(c => c.id === item.components)
                
                return (  
                  <div style={{ display: "grid", gridTemplateColumns: _gridTemplate, gap: "10px", height: "25px" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}># {index + 1}</p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {!component ? <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, opacity: "0.5", fontStyle: "italic"}}>{t("esfWalkThroughPages.esfWalkThrough.noComponent", {defaultValue: "No component"})}</p>
                        : <TooltipString
                          tooltipString={String(component?.name)}
                          contentString={<frames.GridLabel $component={component?.id} $width={_componentWidth}>{
                            <p style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                              {component?.name}
                            </p>
                          }</frames.GridLabel>}
                        />
                      }
                    </div>
                    {addCalVal && <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>{item.calibrationVal}{t("units.g", {defaultValue: "g"})}</p>}
                    <CheckBox
                      checkStyle={CheckStyles.greyTertiary}
                      isChecked={selectedMotors?.includes(item.terminal_id) ?? false}
                      onChange={() => {
                        const current = selectedMotors ?? [];
                        const exists = current.includes(item.terminal_id);
                        let updated: number[];
                      
                        if (addCalVal) {
                          const stationTerminals = station.terminals ?? [];
                          const stationValidTerminalIds = terminals?.filter((t) => stationTerminals.includes(t.terminal_id) && t.fk_terminal_types === 83 ).map((t) => t.terminal_id) ?? [];
                          const filteredOutStation = current.filter((id) => !stationValidTerminalIds.includes(id));
                      
                          if (exists) {
                            updated = filteredOutStation;
                          } else {
                            updated = [...filteredOutStation, item.terminal_id];
                          }
                        } else {
                          updated = exists ? current.filter((id) => id !== item.terminal_id) : [...current, item.terminal_id];
                        }
                      
                        dispatch({ selectedMotors: updated });
                      }}
                    />
                    {locationResults !== undefined && locationResults[item.terminal_id] !== undefined &&
                      <div key={'res_' + item.terminal_id} style={{ height: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {locationResults[item.terminal_id]
                          ? <glyphs.Check fillColor={colors.systemGreen} size={"20px"} /> 
                          : <glyphs.Empty fillColor={colors.systemRed} size={"20px"} />
                        }
                      </div>
                    }
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )
    })
  )
}